<script setup>
import { cloneDeep, isEmpty } from 'lodash';
import {
  ref, reactive, watch, computed,
} from 'vue';
import ModalSimple from '@/components/ModalSimple.vue';
import { modelErrorsToHtmlText } from '../../../../libs/cifra/model_errors';
import ModalSeleccionCurso from './ModalSeleccionCurso.vue';
import ModalAddCompetenciaClave from './ModalAddCompetenciaClave.vue';
import ModalEditDescriptoresOperativos from './ModalEditDescriptoresOperativos.vue';
import ModalAddAsignatura from './ModalAddAsignatura.vue';
import ModalEditCompetenciasEspecificas from './ModalEditCompetenciasEspecificas.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  error: { type: Object, default: null },
});

const emits = defineEmits(['update:modelValue']);

const cursos = reactive(cloneDeep(props.modelValue) || []);

const cursosNoEliminados = computed(() => cursos?.filter((c) => !c._destroy));
const showModalSeleccionCurso = ref(false);

const modalAddCompetenciaClave = ref(null);
const modalEditDescriptoresOperativos = ref(null);
const modalAddAsignatura = ref(null);
const modalEditCompetenciasEspecificas = ref(null);

function nombreParaCurso(curso) {
  return curso.curso.nombre_completo
    || `${curso.curso.nombre} / ${curso.curso.ensennanza.tipo_ensennanza} / ${curso.curso.anno_lectivo.nombre}`;
}

function findCursoById(id) {
  return cursos.find((c) => c.curso_id === id);
}

function showModalAddCompetenciaClave(curso) {
  modalAddCompetenciaClave.value.show(curso);
}

function showModalEditDescriptoresOperativos(competenciaClave) {
  modalEditDescriptoresOperativos.value.show(competenciaClave);
}

function showModalAddAsignatura(curso) {
  modalAddAsignatura.value.show(curso);
}

function showModalEditCompetenciasEspecificas(curso, infoAsignatura) {
  modalEditCompetenciasEspecificas.value.show(curso, infoAsignatura);
}

function cerrarModalSeleccionCurso() {
  showModalSeleccionCurso.value = false;
}

function findSelectorPathByType(path, type) {
  return path.find((p) => p.type === type);
}

function addCurso(nuevoCursoData) {
  if (isEmpty(nuevoCursoData)) { return; }

  const existe = findCursoById(nuevoCursoData.id);
  if (!isEmpty(existe)) {
    if (existe._destroy) {
      delete existe._destroy;
    }
  } else {
    const curso = findSelectorPathByType(nuevoCursoData.path, 'curso');
    const ensennanza = findSelectorPathByType(nuevoCursoData.path, 'ensennanza');
    const annoLectivo = findSelectorPathByType(nuevoCursoData.path, 'anno_lectivo');
    const nuevoCurso = {
      curso_id: nuevoCursoData.id,
      curso: {
        id: curso.id,
        nombre: curso.nombre,
        anno_lectivo: { nombre: annoLectivo.nombre },
        ensennanza: { tipo_ensennanza: ensennanza.nombre, abbr: ensennanza.abbr },
      },
      competencias_clave: [],
      asignaturas: [],
    };

    cursos.push(nuevoCurso);
  }
  showModalSeleccionCurso.value = false;
}

function eliminarCurso(curso) {
  const idx = cursos.findIndex((c) => c.curso.id === curso.curso.id);

  if (idx < 0) { return; }

  if (curso.id == null) {
    cursos.splice(idx, 1);
  } else {
    curso._destroy = true;
  }
}

function addCompetenciaClave(competenciaClave) {
  const curso = findCursoById(competenciaClave.competencia_clave_definicion.curso_id);
  if (!curso) { return; }

  curso.competencias_clave.push(competenciaClave);

  modalAddCompetenciaClave.value.cerrarModal();
}

function eliminarCompetenciaClave(curso, competencia) {
  const idx = curso.competencias_clave.findIndex((cc) => cc.id === competencia.id);

  if (idx < 0) { return; }

  if (competencia.id == null) {
    curso.competencias_clave.splice(idx, 1);
  } else {
    competencia._destroy = true;
    competencia.descriptores.forEach((desc) => { desc._destroy = true; });
  }
}

function competenciasClaveNoEliminadas(curso) {
  return curso.competencias_clave.filter((cc) => !cc._destroy);
}

function editDescriptoresOperativos(competencia) {
  if (isEmpty(competencia)) { return; }

  const curso = findCursoById(competencia.competencia_clave_definicion.curso_id);

  const idx = curso.competencias_clave.findIndex(
    (cc) => cc.competencia_clave_definicion.id === competencia.competencia_clave_definicion.id,
  );

  if (idx < 0) { return; }

  curso.competencias_clave[idx] = competencia;

  modalEditDescriptoresOperativos.value.cerrarModal();
}

function descriptoresNoEliminados(competencia) {
  return competencia.descriptores.filter((desc) => !desc._destroy);
}

function addAsignatura({ cursoId, asignatura, competencias }) {
  // TODO: mover al modal
  const curso = findCursoById(cursoId);
  if (isEmpty(curso) || isEmpty(asignatura) || isEmpty(competencias)) {
    return;
  }

  const asignaturaAdd = curso.asignaturas.find(
    (asg) => asg.asignatura.id === asignatura.id,
  );

  if (asignaturaAdd) {
    if (asignaturaAdd._destroy) { delete asignaturaAdd._destroy; }
    competencias.forEach((nueva) => {
      const compeExiste = asignaturaAdd.competencias_especificas.find(
        // eslint-disable-next-line max-len
        (compe) => compe.competencia_especifica_definicion.id === nueva.competencia_especifica_definicion.id,
      );
      if (compeExiste) {
        if (compeExiste._destroy) { delete compeExiste._destroy; }

        nueva.criterios.forEach((criterio) => {
          const crExiste = compeExiste.criterios.find(
            // eslint-disable-next-line max-len
            (cr) => cr.criterio_evaluacion_definicion.id === criterio.criterio_evaluacion_definicion.id,
          );

          if (crExiste) {
            if (crExiste._destroy) { delete crExiste._destroy; }
          } else {
            compeExiste.criteiros.push(criterio);
          }
        });
      } else {
        asignaturaAdd.competencias_especificas.push(nueva);
      }
    });
  } else {
    const nuevaAsignatura = {
      asignatura_id: asignatura.id,
      situacion_aprendizaje_curso_id: curso.id,
      asignatura,
      competencias_especificas: competencias,
    };
    curso.asignaturas.push(nuevaAsignatura);
  }

  modalAddAsignatura.value.cerrarModal();
}

function editarAsignaturas({ cursoId, asignatura, competencias }) {
  const curso = findCursoById(cursoId);
  if (isEmpty(curso) || isEmpty(asignatura) || isEmpty(competencias)) {
    return;
  }

  const editAsignatura = curso.asignaturas.find(
    (asg) => asg.asignatura.id === asignatura.id,
  );

  if (!editAsignatura) {
    return;
  }

  editAsignatura.competencias_especificas = competencias;

  modalEditCompetenciasEspecificas.value.cerrarModal();
}

function competenciasEspecificasNoEliminadas(infoAsignatura) {
  return infoAsignatura.competencias_especificas.filter((ce) => !ce._destroy);
}

function criteriosNoEliminados(competencia) {
  return competencia.criterios.filter((crit) => !crit._destroy);
}

function eliminarAsignatura(curso, infoAsignatura) {
  if (!curso || !infoAsignatura) { return; }

  const idx = curso.asignaturas.findIndex((asg) => asg.id === infoAsignatura.id);

  if (idx < 0) { return; }

  if (infoAsignatura.id == null) {
    curso.asignaturas.splice(idx, 1);
  } else {
    infoAsignatura.competencias_especificas.forEach(
      (ce) => {
        ce.criterios.forEach((cr) => { cr._destroy = true; });
        ce._destroy = true;
      },
    );
    infoAsignatura._destroy = true;
  }
}

function asignaturasNoEliminadas(curso) {
  return curso.asignaturas.filter((asg) => !asg._destroy);
}

watch(cursos, () => {
  emits('update:modelValue', cursos);
});

</script>

<template>
  <div class="fila-botones">
    <div class="boton m-b">
      <button
        type="button"
        class="btn btn-xs btn-cifra"
        @click="showModalSeleccionCurso = true"
      >
        <i class="fa fa-plus"></i> {{ $t('Anadir_curso') }}
      </button>
    </div>
  </div>
  <div v-if="props.error?.cursos" class="text-danger pull-left">
    {{ modelErrorsToHtmlText(error.cursos) }}
  </div>
  <div
    v-for="curso in cursosNoEliminados"
    :key="curso.curso.id"
    class="cursos"
  >
    <div class="panel panel-default">
      <div class="panel-heading clearfix">
        <div class="panel-title">
          <div class="fila-botones">
            <span class="titulo-lg">
              {{ nombreParaCurso(curso) }}
            </span>
            <div class="boton">
              <a href="#" class="btn btn-xs btn-default m-r-xs" @click="eliminarCurso(curso)">
                <i class="fa fa-trash"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-body">
        <div class="competencias-clave">
          <div class="fila-botones border-bottom m-b">
            <span class="titulo-md p-xxs">
              {{ $t('lomloe_competencias_clave') }}
            </span>
            <div class="boton">
              <a
                href="#"
                class="btn btn-xs btn-cifra"
                @click="showModalAddCompetenciaClave(curso)"
              >
                <i class="fa fa-plus"></i>
              </a>
            </div>
          </div>
          <div v-if="props.error['cursos.competencias_clave']" class="text-danger m-r-xs">
            {{ modelErrorsToHtmlText(error['cursos.competencias_clave']) }}
          </div>
          <ul
            v-for="competenciaClave in competenciasClaveNoEliminadas(curso)"
            :key="competenciaClave.id"
            class="competencias-clave"
          >
            <li>
              <div class="fila-botones m-b">
                <span class="p-xxs">
                  {{ competenciaClave.competencia_clave_definicion.codigo }} -
                  {{ competenciaClave.competencia_clave_definicion.nombre }}
                </span>
                <div class="boton">
                  <a
                    href="#"
                    class="btn btn-xs btn-default m-r-xs"
                    @click="eliminarCompetenciaClave(curso, competenciaClave)"
                  >
                    <i class="fa fa-trash"></i>
                  </a>
                  <a
                    href="#"
                    class="btn btn-xs btn-cifra"
                    @click="showModalEditDescriptoresOperativos(competenciaClave)"
                  >
                    <i class="fa fa-edit"></i>
                  </a>
                </div>
              </div>
              <ul
                v-for="descriptor in descriptoresNoEliminados(competenciaClave)"
                :key="descriptor.id"
                class="descriptores list-unstyled"
              >
                <li class="m-t p-l-md">
                  {{ descriptor.descriptor_operativo_definicion.codigo }} -
                  {{ descriptor.descriptor_operativo_definicion.nombre }}
                </li>
              </ul>
            </li>
          </ul>
        </div>
        <div class="asignaturas">
          <div class="fila-botones border-bottom m-b">
            <span class="titulo-md p-xxs">
              <abbr title="requerido">*</abbr>
              {{ $t('Asignaturas') }}
            </span>
            <div class="boton">
              <a
                href="#"
                class="btn btn-xs btn-cifra"
                @click="showModalAddAsignatura(curso)"
              >
                <i class="fa fa-plus"></i>
              </a>
            </div>
          </div>
          <div v-if="props.error['cursos.asignaturas']" class="text-danger m-r-xs">
            {{ modelErrorsToHtmlText(error['cursos.asignaturas']) }}
          </div>
          <ul
            v-for="infoAsignatura in asignaturasNoEliminadas(curso)"
            :key="infoAsignatura.asignatura.id"
            class="asignaturas"
          >
            <li>
              {{ infoAsignatura.asignatura.nombre }}
              <div class="pull-right">
                <a
                  href="#"
                  class="btn btn-xs btn-default m-r-xs"
                  @click="eliminarAsignatura(curso, infoAsignatura)"
                >
                  <i class="fa fa-trash"></i>
                </a>
                <a
                  href="#"
                  class="btn btn-xs btn-cifra"
                  @click="showModalEditCompetenciasEspecificas(curso, infoAsignatura)"
                >
                  <i class="fa fa-edit"></i>
                </a>
              </div>
              <ul
                v-for="competenciaEspecifica in competenciasEspecificasNoEliminadas(infoAsignatura)"
                :key="competenciaEspecifica.id"
                class="m-t"
              >
                <li>
                  {{ competenciaEspecifica.competencia_especifica_definicion.codigo }} -
                  {{ competenciaEspecifica.competencia_especifica_definicion.nombre }}
                </li>
                <ul class="list-unstyled">
                  <li
                    v-for="criterio in criteriosNoEliminados(competenciaEspecifica)"
                    :key="criterio.id"
                    class="m-t p-l-md"
                  >
                    {{ criterio.criterio_evaluacion_definicion?.codigo }} -
                    {{ criterio.criterio_evaluacion_definicion?.nombre }}
                  </li>
                </ul>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <ModalSimple
    v-model="showModalSeleccionCurso"
    :titulo="$t('Anadir_curso')"
  >
    <ModalSeleccionCurso
      v-if="showModalSeleccionCurso"
      @seleccionado="addCurso($event)"
      @cancelado="cerrarModalSeleccionCurso"
    ></ModalSeleccionCurso>
  </ModalSimple>
  <ModalAddCompetenciaClave
    ref="modalAddCompetenciaClave"
    @anadir="addCompetenciaClave($event)"
  />
  <ModalEditDescriptoresOperativos
    ref="modalEditDescriptoresOperativos"
    @editar="editDescriptoresOperativos($event)"
  />
  <ModalAddAsignatura
    ref="modalAddAsignatura"
    @anadir="addAsignatura($event)"
  />
  <ModalEditCompetenciasEspecificas
    ref="modalEditCompetenciasEspecificas"
    @anadir="editarAsignaturas($event)"
  />
</template>

<style lang="scss" scoped>
.fila-botones {
  display: flex;
  .titulo-lg, .titulo-md, .titulo-xs {
    font-weight: 600;
  }
  .titulo-lg {
    font-size: 14px;
  }
  .titulo-md {
    font-size: 13px;
  }
  .titulo-xs {
    font-size: 12px;
  }
  .boton {
    text-align: right;
    flex: 1;
  }
}
.panel-body {
  display: flex;
  flex-direction: column;
  row-gap: 0.5em;
}

abbr {
  border: 0;
  text-decoration: none;
  color: #fe1d12;
}
</style>
